import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import LanguageToggle from '../components/i18n/LanguageToggle';

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const t = useT();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4 z-10"><LanguageToggle variant="light" /></div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>SELORA</span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-[#E8E4DC] p-8">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="w-14 h-14 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-5">
                <svg className="w-7 h-7 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
              </div>
              <h2 className="text-xl font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{t({ fr: 'Vérifiez votre e-mail', en: 'Check Your Email' })}</h2>
              <p className="text-sm text-[#6B4E37] leading-relaxed">{t({ fr: 'Un lien de réinitialisation a été envoyé à votre adresse.', en: 'Password reset link has been sent to your email.' })}</p>
              <Link to="/login" className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-[#1A2F4A] hover:text-[#D4AF37] transition-colors">
                <ArrowLeft className="w-4 h-4" /> {t({ fr: 'Retour à la connexion', en: 'Back to Login' })}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[#1A2F4A] text-center mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="forgot-password-title">
                {t({ fr: 'Mot de passe oublié', en: 'Forgot Password' })}
              </h1>
              <p className="text-sm text-[#6B4E37] text-center mb-6">
                {t({ fr: 'Entrez votre e-mail et nous vous enverrons un lien de réinitialisation.', en: "Enter your email and we'll send you a reset link." })}
              </p>

              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-5">{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">{t({ fr: 'Adresse e-mail', en: 'Email Address' })}</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com"
                    className="w-full px-4 py-3 border border-[#E8E4DC] rounded-xl focus:outline-none focus:border-[#D4AF37] text-[#1A2F4A] bg-[#FAFAF8]"
                    data-testid="forgot-email" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-[#1A2F4A] hover:bg-[#243a58] text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-60"
                  data-testid="forgot-submit">
                  {loading ? t({ fr: 'Envoi...', en: 'Sending...' }) : t({ fr: 'Envoyer le lien', en: 'Send Reset Link' })}
                </button>
              </form>

              <p className="text-center text-sm text-[#6B4E37] mt-6">
                <Link to="/login" className="inline-flex items-center gap-1 text-[#1A2F4A] font-medium hover:text-[#D4AF37] transition-colors">
                  <ArrowLeft className="w-3.5 h-3.5" /> {t({ fr: 'Retour à la connexion', en: 'Back to Login' })}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
